import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, NgZone, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { ColorSchemeService } from '@examdojo/browser/color-scheme';
import { PageQuery, PageService, SyllabusQuery, TopicLevel2Query, TopicLevel2UIModel } from '@examdojo/category/v2';
import { ButtonComponent } from '@examdojo/core/button';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { LocalizedStringComponent, LocalizePipe } from '@examdojo/core/i18n';
import { IconComponent } from '@examdojo/core/icon';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { distinctUntilDeepChanged } from '@examdojo/core/rxjs';
import { getCollabColorForUserId } from '@examdojo/core/tiptap-react-ui-components';
import type { CollabConfig } from '@examdojo/core/tiptap-react-ui-components';
import { MarkdownViewerComponent } from '@examdojo/core/ui/markdown-viewer';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags } from '@examdojo/models/feature-flag';
import { PlatformService } from '@examdojo/platform';
import {
  ContentBlocksRendererComponent,
  ContentBlockTocComponent,
  StudyNotesEditorComponent,
} from '@examdojo/study-notes';
import { UserQuery } from '@examdojo/user';
import { IonHeader, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  BehaviorSubject,
  combineLatest,
  delay,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  EMPTY,
  filter,
  finalize,
  map,
  merge,
  Observable,
  of,
  startWith,
  switchMap,
  tap,
  timer,
  withLatestFrom,
} from 'rxjs';
import { RootUrlParts } from '../../../app.model';
import { PageLayoutComponent } from '../../../shared/page-layout/page-layout.component';
import { CardComponent } from '../card-link/card-link.component';
import { PAGE_SLUG_PARAM, TOPIC_LEVEL_2_SLUG_PARAM } from '../learn-hub.routes';
import { TopicSelectionPersistenceService } from '../topic-selection-persistence.service';
import { RelatedArticlesComponent } from './related-articles.component';
import { TopicBreadcrumbsComponent } from './topic-breadcrumbs.component';

const LOADING_BAR_MIN_DURATION_MS = 400;
const EMPTY_STATE_DELAY_MS = 200;
const MIN_MOBILE_TOC_HEIGHT_PX = 60;

@Component({
  selector: 'dojo-topic-level-2-detail-view',
  imports: [
    TranslocoPipe,
    RouterLink,
    TopicBreadcrumbsComponent,
    LocalizedStringComponent,
    PageLayoutComponent,
    IconComponent,
    NgClass,
    LocalizePipe,
    AsyncPipe,
    MarkdownViewerComponent,
    ButtonComponent,
    IonHeader,
    IonTitle,
    IonToolbar,
    IconButtonComponent,
    ContentBlockTocComponent,
    CardComponent,
    ContentBlocksRendererComponent,
    StudyNotesEditorComponent,
    MatProgressBarModule,
    RelatedArticlesComponent,
    NgTemplateOutlet,
  ],
  templateUrl: './topic-level-2-detail-view.component.html',
  styleUrl: './topic-level-2-detail-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'relative block w-full safe-area-top safe-area-bottom' },
})
export class TopicLevel2DetailViewComponent implements AfterViewInit {
  constructor(
    private readonly topicLevel2Query: TopicLevel2Query,
    private readonly pageService: PageService,
    private readonly pageQuery: PageQuery,
    private readonly elementRef: ElementRef,
    private readonly platformService: PlatformService,
    private readonly topicSelectionPersistenceService: TopicSelectionPersistenceService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly syllabusQuery: SyllabusQuery,
    private readonly colorSchemeService: ColorSchemeService,
    private readonly route: ActivatedRoute,
    private readonly ngZone: NgZone,
    private readonly featureFlagService: FeatureFlagService<ExamdojoFeatureFlags>,
    private readonly userQuery: UserQuery,
  ) {
    merge(this.fetchPagesOnTopicLevel2Change(), this.saveLastUsedTL2OnTopicChange(), this.scrollToTopOnPageChange())
      .pipe(takeUntilDestroyed())
      .subscribe();
  }
  private readonly topic$: Observable<TopicLevel2UIModel | null> = this.topicLevel2Query.active$;
  private readonly isLoadingPages$ = new BehaviorSubject<boolean>(false);
  readonly scrollableContainer = signal<HTMLElement | undefined>(undefined);
  readonly showMobileToc = signal(false);
  readonly mobileTocMaxHeight = signal(200);
  /** Tracks whether we've attempted to fetch pages at least once for the current topic */
  private readonly hasFetchedPages$ = new BehaviorSubject<boolean>(false);

  private readonly topicSlug$ = this.topic$.pipe(
    filter(Boolean),
    map((topic) => topic.slug),
  );

  /**
   * Derives the active page from route params + store.
   *
   * Uses `route.paramMap` as the driver (emits immediately on navigation) with a `tap`
   * that eagerly syncs the page store from cache. Then `selectAllEntities()` (no debounce)
   * reads the freshly-synced store. This prevents stale content from a different topic
   * flashing briefly, which happens with `entities$` (has `debounceTime(0)`).
   */
  private readonly activePage$ = this.route.paramMap.pipe(
    tap((params) => {
      const topicSlug = params.get(TOPIC_LEVEL_2_SLUG_PARAM);
      if (!topicSlug) {
        return;
      }

      if (this.pageService.hasCachedPages(topicSlug)) {
        this.pageService.restoreFromCache(topicSlug);
      } else {
        this.pageService.clearStore();
      }
    }),
    switchMap((params) =>
      this.pageQuery.selectAllEntities().pipe(
        map((entities) => entities.map((e) => this.pageQuery.toUIModelFn(e))),
        map((pages) => {
          const pageSlug = params.get(PAGE_SLUG_PARAM);
          if (pageSlug) {
            return pages.find((p) => p.slug === pageSlug);
          }
          return pages[0];
        }),
      ),
    ),
    startWith(this.getInitialActivePage()),
  );

  private readonly pagesCount$ = this.pageQuery.entities$.pipe(
    map((pages) => pages.length),
    startWith(0),
  );

  private readonly hasNoPages$ = this.pagesCount$.pipe(map((count) => count === 0));

  readonly showPageBreadcrumb$ = this.pagesCount$.pipe(map((count) => count > 1));

  private readonly isContentEmpty$ = this.activePage$.pipe(
    map((page) => !!page && (!page.content || page.content.length === 0)),
    startWith(false),
  );

  /**
   * Only show empty state after loading completes AND content is actually empty.
   * This prevents a flash of the empty state while content is being fetched.
   * We also wait until we've fetched pages at least once to avoid showing empty state
   * before the initial fetch completes.
   */
  private readonly showEmptyState$ = combineLatest([
    this.hasNoPages$,
    this.isContentEmpty$,
    this.isLoadingPages$,
    this.hasFetchedPages$,
  ]).pipe(
    map(
      ([hasNoPages, isContentEmpty, isLoading, hasFetched]) =>
        hasFetched && !isLoading && (hasNoPages || isContentEmpty),
    ),
    distinctUntilChanged(),
    switchMap((shouldShow) => (shouldShow ? timer(EMPTY_STATE_DELAY_MS).pipe(map(() => true)) : of(false))),
    startWith(false),
  );

  /**
   * Shows loading bar for at least LOADING_BAR_MIN_DURATION_MS.
   * When loading starts: immediately show bar
   * When loading ends: wait for minimum duration before hiding
   */
  private readonly showLoadingBar$ = this.isLoadingPages$.pipe(
    distinctUntilChanged(),
    switchMap((isLoading) => {
      if (isLoading) {
        return of(true);
      }
      // Delay hiding by minimum duration
      return of(false).pipe(delay(LOADING_BAR_MIN_DURATION_MS));
    }),
    startWith(false),
  );

  private readonly commentsEnabled$ = this.featureFlagService.select(
    ExamDojoFeatureFlag.StudyNotesComments,
  ) as Observable<boolean>;

  private readonly collabConfig$: Observable<CollabConfig | undefined> = combineLatest([
    this.commentsEnabled$,
    this.activePage$,
    this.userQuery.selectActive(),
    this.userQuery.profilePic$,
    this.userQuery.oAuthProfilePic$,
  ]).pipe(
    map(([enabled, page, user, profilePicUrl, oAuthProfilePicUrl]) => {
      if (!enabled || !page?.slug || !user) {
        return undefined;
      }

      const name = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email;

      return {
        roomId: `study-notes/${page.slug}`,
        user: {
          id: user.id,
          name,
          color: getCollabColorForUserId(user.id),
          avatarUrl: profilePicUrl ?? oAuthProfilePicUrl ?? undefined,
        },
      } satisfies CollabConfig;
    }),
    distinctUntilDeepChanged(),
  );

  readonly state = connectState({
    topic: this.topic$,
    showLoadingBar: this.showLoadingBar$,
    isMobile: this.platformService.isSmall$,
    activePage: this.activePage$,
    showEmptyState: this.showEmptyState$,
    darkMode: this.colorSchemeService.hasDarkMode$,
    showPageBreadcrumb: this.showPageBreadcrumb$,
    formulaBookletUrl: this.syllabusQuery.activeFormularBooklet$,
    commentsEnabled: this.commentsEnabled$,
    collabConfig: this.collabConfig$,
  });

  protected readonly RootUrlParts = RootUrlParts;

  ngAfterViewInit() {
    const scrollableElement = this.elementRef.nativeElement.closest('.main-content-area') as HTMLElement;
    this.scrollableContainer.set(scrollableElement);
  }

  toggleMobileToc() {
    this.showMobileToc.update((v) => !v);
  }

  onGutterDragStart(startEvent: MouseEvent | TouchEvent) {
    startEvent.preventDefault();
    const startY = 'touches' in startEvent ? startEvent.touches[0].clientY : startEvent.clientY;
    const startHeight = this.mobileTocMaxHeight();

    let rafId: number | null = null;
    const onMove = (e: MouseEvent | TouchEvent) => {
      if ('touches' in e) {
        e.preventDefault();
      }
      if (rafId) {
        return;
      }
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const currentY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        const delta = currentY - startY;
        this.ngZone.run(() => {
          this.mobileTocMaxHeight.set(Math.max(MIN_MOBILE_TOC_HEIGHT_PX, startHeight + delta));
        });
      });
    };

    const onEnd = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };

    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onEnd);
      document.addEventListener('touchmove', onMove, { passive: false });
      document.addEventListener('touchend', onEnd);
    });
  }

  private getInitialActivePage() {
    // Eagerly restore cached pages into the store so the initial value is correct.
    // Without this, the store may still contain a different topic's pages when the
    // component is recreated (navigating back from a different topic).
    const topicSlug = this.route.snapshot.paramMap.get(TOPIC_LEVEL_2_SLUG_PARAM);
    if (!topicSlug || !this.pageService.hasCachedPages(topicSlug)) {
      // No cache for this topic — store may have stale pages from a different topic.
      return undefined;
    }

    this.pageService.restoreFromCache(topicSlug);
    const pages = this.pageQuery.getAllUIEntities();
    const pageSlug = this.route.snapshot.paramMap.get(PAGE_SLUG_PARAM);
    if (pageSlug) {
      return pages.find((p) => p.slug === pageSlug);
    }
    return pages[0];
  }

  private scrollToTopOnPageChange() {
    return this.route.paramMap.pipe(
      map((params) => `${params.get(TOPIC_LEVEL_2_SLUG_PARAM)}/${params.get(PAGE_SLUG_PARAM)}`),
      distinctUntilChanged(),
      withLatestFrom(this.platformService.isSmall$),
      filter(([, isMobile]) => !isMobile),
      tap(() => {
        requestAnimationFrame(() => {
          const container = this.scrollableContainer();
          if (container) {
            container.scrollTop = 0;
          }
        });
      }),
    );
  }

  private fetchPagesOnTopicLevel2Change() {
    return this.topic$.pipe(
      filter(Boolean),
      distinctUntilKeyChanged('slug'),
      switchMap((topicLevel2) => {
        // Pages already cached for this topic — restore from cache without refetching
        if (this.pageService.hasCachedPages(topicLevel2.slug)) {
          this.pageService.restoreFromCache(topicLevel2.slug);
          this.hasFetchedPages$.next(true);
          return EMPTY;
        }

        // Reset fetch state for new topic
        this.hasFetchedPages$.next(false);
        this.isLoadingPages$.next(true);
        return this.pageService.fetchForTopicLevel2(topicLevel2.slug, { useCache: true }).pipe(
          tap(() => this.hasFetchedPages$.next(true)),
          finalize(() => this.isLoadingPages$.next(false)),
          this.errorHandlerService.catchHttpErrors(() => EMPTY),
        );
      }),
    );
  }

  private saveLastUsedTL2OnTopicChange() {
    return combineLatest([this.topic$, this.route.paramMap]).pipe(
      filter(([topic]) => !!topic),
      map(([topic, params]) => ({
        topic: topic!,
        pageSlug: params.get(PAGE_SLUG_PARAM) ?? undefined,
      })),
      distinctUntilChanged((prev, curr) => prev.topic.id === curr.topic.id && prev.pageSlug === curr.pageSlug),
      tap(({ topic, pageSlug }) => {
        const syllabusSlug = topic.topicLevel1?.syllabus?.slug;
        const topicLevel1Slug = topic.topicLevel1?.slug;
        const courseLevel = this.syllabusQuery.getProp('course_level') ?? null;
        if (syllabusSlug && topicLevel1Slug) {
          this.topicSelectionPersistenceService.setLastUsedPage(
            syllabusSlug,
            courseLevel,
            topicLevel1Slug,
            topic.slug,
            pageSlug,
          );
        }
      }),
    );
  }
}
